import { useCallback, useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router'

import { useAppContext } from '../../../../hooks/useAppContext'
import { initialTextField, TextField } from '../../../../lib/field'
import { useAuthContext } from '../../../Authentication/hooks/useAuthContext'

// Form field structure definition
class FormFields {
    abbreviation!: TextField
    content!: TextField
}

type FormFieldsKeys = keyof FormFields

// Initial state for form fields
const initialFormFields: FormFields = {
    abbreviation: initialTextField,
    content: initialTextField
}

// Action type for reducer
interface Action {
    fieldKey: FormFieldsKeys
    payload: TextField
}

// Reducer to handle form state updates
const reducer = (form: FormFields, { fieldKey, payload }: Action): FormFields => {
    return {
        ...form,
        [fieldKey]: payload
    }
}

// Hook return type definition
export interface FormHookState {
    form: FormFields
    isLoading: boolean
    updateField(fieldKey: FormFieldsKeys, fieldValue: string): void
    validateField(fieldKey: FormFieldsKeys): boolean
    submit(callback: () => void): void
}

/**
 * Custom hook for managing snippet form state and operations
 * @param snippet Optional existing snippet for edit mode
 */
export const useForm = (snippet?: { id: string; abbreviation: string; content: string }): FormHookState => {
    const { user } = useAuthContext().user
    const { isLoading, createSnippet, editSnippet } = useAppContext().snippets
    const [form, dispatch] = useReducer(reducer, Object.assign({}, initialFormFields))
    const navigate = useNavigate()
    const { snippets } = useAppContext().snippets

    // Updates field value and clears any existing error
    const updateField = useCallback((fieldKey: FormFieldsKeys, fieldValue: string) => {
        dispatch({
            fieldKey,
            payload: { value: fieldValue, error: null }
        })
    }, [])

    // Sets error message for a specific field
    const updateFieldError = useCallback(
        (fieldKey: FormFieldsKeys, errorMessage: string) => {
            dispatch({
                fieldKey,
                payload: {
                    value: form[fieldKey].value,
                    error: errorMessage
                }
            })
        },
        [form]
    )

    const getSnippetByAbbreviation = useCallback(
        (abbreviation: string) => {
            return snippets.find((snippet) => snippet.abbreviation === abbreviation)
        },
        [snippets]
    )

    // Validates individual field
    const validateField = useCallback(
        (fieldKey: FormFieldsKeys): boolean => {
            const value = form[fieldKey].value

            if (!value) {
                updateFieldError(fieldKey, 'Required')
                return false
            }

            if (fieldKey === 'abbreviation') {
                if (snippet?.abbreviation !== value) {
                    // Check if the abbreviation is already in use from snippets list in redux store
                    const existingSnippet = getSnippetByAbbreviation(value)
                    if (existingSnippet) {
                        updateFieldError(fieldKey, 'Abbreviation already in use')
                        return false
                    }
                }
            }

            return true
        },
        [form, updateFieldError, getSnippetByAbbreviation, snippet]
    )

    // Validates all form fields
    const validateForm = useCallback((): boolean => {
        const validations: boolean[] = []
        validations.push(validateField('abbreviation'))
        validations.push(validateField('content'))
        return !validations.some((i: boolean) => i === false)
    }, [validateField])

    // Handles form submission for both create and update operations
    const submit = useCallback(
        async (callback: () => void) => {
            const userId = user?._id
            if (!userId) return
            if (!validateForm()) return

            const data = {
                abbreviation: form.abbreviation.value?.replace(/\//g, '') as string,
                content: form.content.value as string
            }

            try {
                if (snippet?.id) {
                    await editSnippet(snippet.id, data)
                } else {
                    await createSnippet(userId, data)
                }
                navigate(`/settings/snippets/${data.abbreviation}`)
                callback()
            } catch (err) {
                // Error handling is done in useSnippets
            }
        },
        [user?._id, validateForm, form, snippet, createSnippet, editSnippet, navigate]
    )

    // Initializes form with existing snippet data when in edit mode
    // Initialize form with existing snippet data when in edit mode
    useEffect(() => {
        const snippetId = snippet?.id
        if (!snippetId) {
            // Only update fields if we haven't initialized this snippet yet
            updateField('abbreviation', '')
            updateField('content', '')
            return
        }

        // Only update fields if we haven't initialized this snippet yet
        updateField('abbreviation', snippet.abbreviation)
        updateField('content', snippet.content)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snippet?.id])

    return {
        form,
        isLoading,
        updateField,
        validateField,
        submit
    }
}
